import { skipToken } from '@reduxjs/toolkit/query'
import { ChangeEvent, FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Button, MenuItem, Paper, TextField, Typography } from '@mui/material'

import { Currency } from 'types/currency'
import { Wallet } from 'types/wallet'

import { useMediaSize } from 'hooks/useMediaSize'

import { useGetOrganizationWalletsQuery } from 'services/organizations'

import AddWalletModal from 'components/AddWalletModal'
import EmptyList from 'components/EmptyList'
import { TModalVariant } from 'components/Modal/types'
import OrganizationWalletCard from 'components/OrganizationWalletCard'
import { Option } from 'components/Select'
import DataTable from 'components/Table'
import { HeadCell } from 'components/Table/types'

interface Props {
  organizationId: string
  currencies: Currency[]
}

const OrganizationWallets: FC<Props> = ({ organizationId, currencies }) => {
  const { t } = useTranslation()
  const { isPhone } = useMediaSize()
  const [currency, setCurrency] = useState(currencies[0]?.code)
  const [modalMode, setModalMode] = useState<TModalVariant>(null)

  const {
    data: wallets = [],
    isFetching,
    refetch,
  } = useGetOrganizationWalletsQuery(
    currency ? { organizationId, body: { currency, excludeEmpty: false } } : skipToken,
    { refetchOnMountOrArgChange: true }
  )

  const currenciesOptions: Option[] = useMemo(
    () => currencies?.map((currency) => ({ label: currency.code, value: currency.code })),
    [currencies]
  )

  // update wallets if currency has not changed
  const refetchOrganizationWallets = (activeCurrency: string) => {
    setCurrency(activeCurrency)
    activeCurrency === currency && refetch()
  }

  const headCells: HeadCell<Wallet>[] = [
    {
      id: 'name',
      label: t('Pages.Organizations.Name'),
    },
    {
      id: 'type',
      label: t('Pages.Organizations.Type'),
    },
    {
      id: 'balance',
      label: t('Pages.Organizations.Balance'),
    },
    {
      id: 'reserved',
      label: t('Pages.Organizations.Reserved'),
    },
  ]

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h3">{t('Pages.Organizations.Wallets')}</Typography>
        <TextField
          label={t('Currency')}
          select
          value={currency}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setCurrency(event.target.value)
          }}
          sx={{ width: 160 }}
        >
          {currenciesOptions?.map((o, index) => (
            <MenuItem key={index} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Paper sx={{ p: 2, mt: 2 }}>
        {isPhone ? (
          wallets.length > 0 ? (
            wallets.map((wallet) => <OrganizationWalletCard key={wallet.id} wallet={wallet} />)
          ) : (
            <EmptyList height={58} />
          )
        ) : (
          <DataTable headCells={headCells} data={wallets} isLoading={isFetching} />
        )}
      </Paper>
      <Button variant="contained" sx={{ mt: 3 }} onClick={() => setModalMode('create')}>
        {t('Pages.Organizations.AddNewWallet')}
      </Button>
      {modalMode && (
        <AddWalletModal
          refetchOrganizationWallets={refetchOrganizationWallets}
          organizationId={organizationId}
          handleClose={() => setModalMode(null)}
        />
      )}
    </Paper>
  )
}

export default OrganizationWallets
